import { graphql, useStaticQuery } from 'gatsby';
import React, { FC } from 'react';
import { PublicQuote } from '../quote/PublicQuote';
import { PublicSignupBanner } from '../signup-banner/PublicSignupBanner';
import { WistiaVideos } from '../wistia-videos/WistiaVideos';
import { HomeBigTitle } from './big-title/HomeBigTitle';
import { HomeCarousel } from './carousel/HomeCarousel';
import { HomeClientNeeds } from './personas/HomeClientNeeds';
import { HomeLogos } from './logos/HomeLogos';
import { SmallTitle } from '../small-title/SmallTitle';
import { HomeTextImageBlock } from './text-image-block/HomeTextImageBlock';
import { HomeTopTitle } from './top-title/HomeTopTitle';

type Props = {
  pageName: string;
};

export const HomeSlices: FC<Props> = ({ pageName }) => {
  const data = useStaticQuery(query);
  const node = data.prismic.allHome_public_v2s.edges[0].node;
  const slices = node.body;
  return slices.map(
    (slice: { type: string; fields: any; primary: any; label: any }) => (
      <div key={slice.type}>
        {slice.type === 'top_title' && (
          <HomeTopTitle {...slice.primary} pageName={pageName} />
        )}
        {slice.type === 'carousel' && (
          <HomeCarousel
            slides={slice.fields}
            linkText={slice.primary.link_text}
          />
        )}
        {slice.type === 'small_title' && <SmallTitle {...slice.primary} />}
        {slice.type === 'video_feedbacks' && (
          <WistiaVideos videos={slice.fields} />
        )}
        {slice.type === 'logos' && <HomeLogos logos={slice.fields} />}
        {slice.type === 'big_title' && (
          <HomeBigTitle {...slice.primary} pageName={pageName} />
        )}
        {slice.type === 'text_with_video_block' && (
          <HomeTextImageBlock data={slice.primary} label={slice.label} />
        )}
        {slice.type === 'quote' && (
          <PublicQuote {...slice.primary} linkText={node.quote_link_text} />
        )}
        {slice.type === 'needs' && (
          <HomeClientNeeds
            personas={slice.fields}
            title={slice.primary.title}
          />
        )}
        {slice.type === 'signup_banner' && (
          <PublicSignupBanner {...slice.primary} pageName={pageName} />
        )}
      </div>
    ),
  );
};

const query = graphql`
  {
    prismic {
      allHome_public_v2s {
        edges {
          node {
            body {
              ... on PRISMIC_Home_public_v2BodyCarousel {
                type
                primary {
                  link_text
                }
                fields {
                  job
                  link
                  picture
                  quote
                  name
                }
              }
              ... on PRISMIC_Home_public_v2BodyVideo_feedbacks {
                type
                fields {
                  name
                  subtitle
                  wistia_script_assets
                  wistia_script_medias
                  wistia_video_div
                }
              }
              ... on PRISMIC_Home_public_v2BodyLogos {
                type
                fields {
                  logo
                }
              }
              ... on PRISMIC_Home_public_v2BodyText_with_video_block {
                type
                label
                primary {
                  block_text
                  block_title
                  block_type
                  link
                  link_text
                  video {
                    ... on PRISMIC__ImageLink {
                      url
                    }
                  }
                }
              }
              ... on PRISMIC_Home_public_v2BodyQuote {
                type
                primary {
                  image
                  quote_job
                  quote_link
                  quote_name
                  quote_text
                }
              }
              ... on PRISMIC_Home_public_v2BodyNeeds {
                type
                fields {
                  picture
                  text_1
                  text_2
                  text_3
                  type
                }
                primary {
                  title
                }
              }
              ... on PRISMIC_Home_public_v2BodySignup_banner {
                type
                primary {
                  title
                  subtitle
                  button_text
                }
              }
              ... on PRISMIC_Home_public_v2BodySmall_title {
                type
                primary {
                  subtitle
                  title
                }
              }
              ... on PRISMIC_Home_public_v2BodyTop_title {
                type
                primary {
                  button_link
                  button_text
                  subtitle
                  title
                  small_title
                }
              }
              ... on PRISMIC_Home_public_v2BodyBig_title {
                type
                primary {
                  button_link
                  button_text
                  title
                  subtitle
                }
              }
            }
            quote_link_text
          }
        }
      }
    }
  }
`;
